import {
  Alert,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { validateField } from '../../../../utils/validation';
import {
  useCollectionCreate,
  type CollectionCreateRefusal,
} from '../../../../hooks/create/useCollectionCreate';
import type { CreatedProject } from '../../../../hooks/data/useProjects';
import {
  destinationProblemMessage,
  type CreateDestination,
} from '../destination/createDestination';
import { PrivateToMeField, privateCollectionNotice } from '../PrivateToMeField';
import { useAppToast } from '../../../../hooks/ui/useAppToast';
import { InsideChip } from './InsideChip';

interface CollectionCreateFormProps {
  /** Where it will be created. */
  readonly parent: CreateDestination;
  /** Open the picker to choose another parent ("Inside" chip). */
  readonly onChangeParent: () => void;
  readonly onCreated: (collection: CreatedProject) => void;
  /** A sibling already has the name: use that one instead. */
  readonly onUseExisting: (collection: { readonly id: string; readonly name: string }) => void;
  /** Prefilled from the picker's search. */
  readonly initialName?: string;
  /** Created from inside the picker, as a destination (analytics). */
  readonly inline?: boolean;
  /** The create is in flight (the Create modal holds while it is). */
  readonly onBusyChange?: (busy: boolean) => void;
}

function refusalText(refusal: CollectionCreateRefusal, parent: CreateDestination, name: string) {
  switch (refusal.kind) {
    case 'taken':
      // The sibling's own name can differ in case from what was typed.
      return `${parent.name} already has a collection with this name.`;
    case 'trash':
      return `A collection called "${name.trim()}" is in Trash. Restore it, or choose another name.`;
    case 'parent':
      return destinationProblemMessage(refusal.problem, parent);
    case 'other':
      return refusal.message;
  }
}

/**
 * New collection, the one form the create modal uses: name, where it goes
 * (the Inside chip), Private to me, and every refusal said in place. Enter
 * submits; a double submit is ignored while one is in flight.
 */
export function CollectionCreateForm(props: CollectionCreateFormProps) {
  const { parent, onChangeParent, onCreated, onUseExisting } = props;
  const [name, setName] = useState(props.initialName ?? '');
  const [isPrivate, setIsPrivate] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  // Naming is why this form is open, so the field takes focus at once.
  useEffect(() => nameRef.current?.focus(), []);
  const create = useCollectionCreate(parent, props.inline ?? false);
  const toast = useAppToast();
  const { onBusyChange } = props;
  useEffect(() => {
    onBusyChange?.(create.isPending);
  }, [create.isPending, onBusyChange]);
  const nameError = name.trim().length > 0 ? validateField('project', 'name', name.trim()) : null;
  const canSubmit = name.trim().length > 0 && !nameError && !create.isPending;
  const inheritedFrom =
    parent.type === 'project' && parent.privacyMode === 'PRIVATE' ? parent.name : undefined;

  const submit = async () => {
    if (!canSubmit) return;
    const created = await create.submit({ name, isPrivate });
    if (!created) return;
    // Every caller confirms a private create the same way, so it lives here.
    const notice = privateCollectionNotice(created.name, created.privacyMode);
    if (notice) toast({ description: notice, status: 'success' });
    onCreated(created);
  };
  const refusal = create.refusal;
  const existingId = refusal?.kind === 'taken' ? refusal.existingId : undefined;

  return (
    <VStack spacing={4} align="stretch">
      <FormControl isInvalid={!!nameError}>
        <Input
          aria-label="Collection name"
          placeholder="Collection name"
          size="lg"
          ref={nameRef}
          value={name}
          isDisabled={create.isPending}
          onChange={(event) => {
            setName(event.target.value);
            create.clearRefusal();
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
            event.preventDefault();
            void submit();
          }}
        />
        <FormErrorMessage>{nameError}</FormErrorMessage>
      </FormControl>
      <InsideChip parent={parent} onChange={onChangeParent} isDisabled={create.isPending} />
      <PrivateToMeField
        isChecked={isPrivate}
        onChange={setIsPrivate}
        isDisabled={create.isPending}
        inheritedFrom={inheritedFrom}
      />
      {refusal && (
        <Alert
          status="warning"
          borderRadius="lg"
          flexDirection="column"
          alignItems="stretch"
          gap={2}
        >
          <Text fontSize="sm">{refusalText(refusal, parent, name)}</Text>
          {existingId && (
            <Button
              size="sm"
              variant="outline"
              alignSelf="flex-start"
              onClick={() => onUseExisting({ id: existingId, name: name.trim() })}
            >
              Use it
            </Button>
          )}
          {refusal.kind === 'parent' && (
            <Button size="sm" variant="outline" alignSelf="flex-start" onClick={onChangeParent}>
              Choose another place
            </Button>
          )}
        </Alert>
      )}
      {/* No Cancel: the modal's back arrow and close already leave. */}
      <Button
        variant="primary"
        isLoading={create.isPending}
        loadingText="Creating…"
        isDisabled={!canSubmit || (refusal?.kind === 'taken' && !!existingId)}
        onClick={() => void submit()}
      >
        Create collection
      </Button>
    </VStack>
  );
}
